import React, { ReactNode, createContext, useState, useContext, useCallback } from 'react';
import { Accordion } from '@ver-uds/uswds-react';

const COLLAPSED_MAX_HEIGHT = 170;

export interface CollapsableCardContextValue {
  collapsedMaxHeight: number;
  tooLongIds: string[];
  initializeCollapsable: (id: string, ref: React.MutableRefObject<any>) => void;
  isTooLong: (id: string) => boolean;
}

const CollapsableCardContext = createContext<CollapsableCardContextValue | null>(null);

export const CollapsableCardProvider = ({ children }: { children: ReactNode }): JSX.Element => {
  const [tooLongIds, setTooLongIds] = useState<string[]>([]);
  const isTooLong = useCallback((id: string): boolean => tooLongIds.includes(id), [tooLongIds]);

  const initializeCollapsable = (id: string, ref: React.MutableRefObject<any>): void => {
    if (ref.current.clientHeight > COLLAPSED_MAX_HEIGHT) {
      setTooLongIds([...tooLongIds, id]);
    }
  };

  return (
    <Accordion multiple>
      <CollapsableCardContext.Provider
        value={{
          collapsedMaxHeight: COLLAPSED_MAX_HEIGHT,
          tooLongIds,
          initializeCollapsable,
          isTooLong,
        }}
      >
        {children}
      </CollapsableCardContext.Provider>
    </Accordion>
  );
};

export const useCollapsableCardContext = (): CollapsableCardContextValue => {
  const contextValue = useContext(CollapsableCardContext);
  if (!contextValue) {
    throw new Error('useCollapsableCardContext must be used inside an <CollapsableProvider>');
  }
  return contextValue;
};
